/**
 * Hook to ensure user has default apps and tools installed
 * Automatically initializes defaults on app load if missing
 */

import { useEffect, useRef, useState } from 'react';
import { createFileLogger } from '../utils/logger';
import fetchService from '../services/fetchService';
import { API_BASE_URL } from '../api/config';
import useUserStore from '../stores/userStore';
import { perf } from '../utils/perfInstrumentation';

const log = createFileLogger('DEFAULTS_INITIALIZER', { enabled: false });

interface DefaultsStatus {
  hasAllDefaults: boolean;
  installedApps: string[];
  installedToolsCount: number;
  missingApps: string[];
  systemToolsAvailable: number;
}

export function useDefaultsInitializer() {
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const profile = useUserStore(state => state.profile);
  // Once-guard: run the check exactly once per mount, regardless of how many
  // times the profile object reference changes (finding #11).
  const checkedRef = useRef(false);

  useEffect(() => {
    if (!profile) {
      setLoading(false);
      return;
    }

    if (checkedRef.current) return;
    checkedRef.current = true;

    const checkAndInitializeDefaults = async () => {
      try {
        log.info('[useDefaultsInitializer] Checking defaults status...');
        perf.increment('defaultsStatusCheckCount');

        // Check if user has defaults
        const status = await fetchService(
          `${API_BASE_URL}/user/defaults-status`
        ) as DefaultsStatus;

        log.info('[useDefaultsInitializer] Status:', status);

        if (!status.hasAllDefaults) {
          log.info('[useDefaultsInitializer] Missing defaults, initializing...');

          // Initialize defaults
          const result = await fetchService(
            `${API_BASE_URL}/user/initialize-defaults`,
            {
              method: 'POST'
            }
          );

          log.info('[useDefaultsInitializer] ✅ Initialized defaults:', result);
          setInitialized(true);
          
          // Reload user profile to get updated apps/tools
          useUserStore.getState().fetchProfile();
        } else {
          log.info('[useDefaultsInitializer] ✅ All defaults already installed');
          setInitialized(true);
        }
      } catch (error) {
        log.error('[useDefaultsInitializer] Failed to check/initialize defaults:', error);
        // Don't block app load on failure
        setInitialized(true);
      } finally {
        setLoading(false);
      }
    };

    checkAndInitializeDefaults();
  }, [profile]);

  return { initialized, loading };
}
